import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { commentBodySchema } from '@/api/adapter/proposalReviews';
import { z } from 'zod';
import { MainScreen } from '@/components/shared/MainScreen/MainScreen';
import { Avatar, Button, Spinner } from '@/components/ui';
import {
  EscrowPaymentModal,
  isEscrowModalDismissed,
} from '@/components/features/payments/EscrowPaymentModal';
import {
  useProjectView,
  useProjectBrief,
  useProjectParticipants,
} from '@/hooks/useProjectViews';
import { useProjectCommand } from '@/hooks/useProjectCommand';
import {
  useProposalPresentation,
  useProposalComments,
  useProposalCommentWriter,
} from '@/hooks/useProposalReviews';
import { useBalance } from '@/hooks/useFinance';
import { useAuthStore } from '@/stores/authStore';
import { profileImageUrl } from '@/hooks/useProfile';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import type { ProjectView, ProposalView } from '@/types/current-project';
import type { ProjectCommand } from '@/types/project-command';
import { ProposalMilestones } from './components/BuildScope/ProposalMilestones';
import { ProposalReviewHistory } from './components/BuildScope/ProposalReviewHistory';
import {
  proposalCost,
  deliveryPreferenceLabel,
} from '@/lib/project-presentation';
import './ScopeReviewPage.css';

export default function ScopeReviewPage() {
  const { id } = useParams<{ id: string }>();
  const query = useProjectView(id);
  const principal = useAuthStore((s) => s.session?.principalId);
  const account = useAuthStore((s) => s.session?.accountId);
  if (query.isPending)
    return (
      <div role="status" className="p-8">
        <Spinner /> Loading scope…
      </div>
    );
  if (query.isError)
    return (
      <div role="alert" className="space-y-4 p-8">
        <p>{query.error.message}</p>
        <Button onClick={() => void query.refetch()}>Retry scope</Button>
        <Link to="/projects">Back to Projects</Link>
      </div>
    );
  if (account !== query.data.client)
    return (
      <div className="space-y-4 p-8">
        <h1>Access Restricted</h1>
        <p>Only the client who owns this project can review its scope.</p>
        <Link to={'/projects/' + id}>Back to Project</Link>
      </div>
    );
  const proposal =
    query.data.proposals.find((p) => p.status === 'PendingApproval') ??
    query.data.proposals.slice(-1)[0];
  if (!proposal)
    return (
      <div className="p-8">
        <p>No proposal has been delivered yet.</p>
        <Link to={'/projects/' + id}>Back to Project</Link>
      </div>
    );
  return (
    <ScopeReview
      key={principal + ':' + proposal.proposalId}
      project={query.data}
      proposal={proposal}
    />
  );
}
const reviewDraftSchema = z.object({
  decision: z.enum(['accept', 'reject']).nullable(),
  message: z.string(),
  reviewKey: z.string().nullable(),
  proposalRevision: z
    .number()
    .int()
    .nonnegative()
    .safe()
    .nullable()
    .default(null),
});
function ScopeReview({
  project,
  proposal,
}: {
  project: ProjectView;
  proposal: ProposalView;
}) {
  const principal = useAuthStore((s) => s.session?.principalId);
  const storageKey =
    'scope-response:' +
    principal +
    ':' +
    project.projectId +
    ':' +
    proposal.proposalId;
  const [draft, setDraft] = useState<z.infer<typeof reviewDraftSchema>>(() => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) return reviewDraftSchema.parse(JSON.parse(saved));
    } catch {
      /* server scope remains authoritative */
    }
    return {
      decision: null,
      message: '',
      reviewKey: null,
      proposalRevision: null,
    };
  });
  const [notice, setNotice] = useState('');
  const [emailCopied, setEmailCopied] = useState(false);
  const [showEscrow, setShowEscrow] = useState(false);
  const [explained, setExplained] = useState(isEscrowModalDismissed);
  const command = useProjectCommand(project.projectId);
  const writer = useProposalCommentWriter(
    project.projectId,
    proposal.proposalId
  );
  const presentation = useProposalPresentation(
    project.projectId,
    proposal.proposalId
  );
  const comments = useProposalComments(project.projectId, proposal.proposalId);
  const participants = useProjectParticipants(project.projectId);
  const brief = useProjectBrief(project.projectId);
  const balance = useBalance();
  const { currency } = useDaoConfig();
  const definition = {
    title: proposal.title,
    description: proposal.description,
    milestones: proposal.milestones.map((m) => m.definition),
  };
  const cost = proposalCost(definition);
  const coordinator = participants.data?.coordinator;
  const name =
    coordinator?.profiles?.worker?.name ??
    coordinator?.displayName ??
    'Project Leader';
  const lastMessage = comments.data?.pages
    .flatMap((page) => page.items)
    .filter(
      (item) =>
        item.authorAccount === project.coordinator &&
        JSON.stringify(item.definition) === JSON.stringify(definition) &&
        (item.presentation?.revision ?? 0) ===
          (presentation.data?.revision ?? 0)
    )
    .slice(-1)[0]?.message;
  const reviewKey = JSON.stringify([proposal, project.planning]);
  const decision = draft.reviewKey === reviewKey ? draft.decision : null;
  const active =
    proposal.status === 'PendingApproval' &&
    !project.cancelled &&
    !project.completed &&
    !project.planning.frozen &&
    !project.activeDisputeId &&
    !project.proposals.some((p) => p.status === 'Approved');
  const locked =
    command.busy ||
    command.pending ||
    command.blocked ||
    writer.busy ||
    writer.blocked ||
    !!writer.pending;
  const delivery = project.planning.status === 'Delivered';
  const execution = project.planning.status === 'Completed';
  const available = balance.data?.available;
  const hasFunds = available !== undefined && BigInt(available) >= BigInt(cost);
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(draft));
    } catch {
      setNotice(
        'The response draft cannot be retained for reload. Keep this page open.'
      );
    }
  }, [draft, storageKey]);
  async function finish(
    result: Awaited<ReturnType<typeof command.run>>,
    kind: ProjectCommand['kind'] | undefined = command.saved?.command.kind
  ) {
    if (!result) return;
    setNotice('Your decision is confirmed.');
    if (draft.message.trim()) {
      const saved = await writer.send(
        draft.message,
        (draft.proposalRevision ?? proposal.revision) +
          (kind === 'proposal-changes' || kind === 'proposal-approve' ? 1 : 0)
      );
      if (!saved)
        setNotice(
          'Your decision is confirmed; only the comment still needs saving.'
        );
    }
    setDraft({
      decision: null,
      message: '',
      reviewKey: null,
      proposalRevision: null,
    });
  }
  async function submit() {
    if (!active || locked || !decision) return;
    if (draft.message.trim()) {
      try {
        commentBodySchema.shape.message.parse(draft.message);
      } catch (reason) {
        setNotice((reason as Error).message);
        return;
      }
    }
    if (decision === 'reject' && !draft.message.trim()) {
      setNotice('Describe the changes you need before rejecting the scope.');
      return;
    }
    if (decision === 'accept' && execution) {
      if (!hasFunds) {
        setNotice(
          'Your available balance is insufficient for execution. Open funding below.'
        );
        return;
      }
      if (!explained) {
        setShowEscrow(true);
        return;
      }
    }
    const input: ProjectCommand =
      decision === 'reject'
        ? {
            kind: 'proposal-changes',
            projectId: project.projectId,
            proposalId: proposal.proposalId,
            payload: { reference: draft.message },
          }
        : delivery
          ? {
              kind: 'accept-delivery',
              projectId: project.projectId,
              payload: { expectedRevision: project.planning.revision },
            }
          : {
              kind: 'proposal-approve',
              projectId: project.projectId,
              proposalId: proposal.proposalId,
              payload: { expectedRevision: proposal.revision },
            };
    await finish(await command.run(input), input.kind);
  }
  return (
    <>
      <EscrowPaymentModal
        isOpen={showEscrow}
        onClose={() => setShowEscrow(false)}
        onProceedToPayment={() => {
          setExplained(true);
          setNotice(
            'Review the execution cost and submit your response to fund execution.'
          );
        }}
      />
      <MainScreen
        backRoute={'/projects/' + project.projectId}
        backTitle={project.title}
        contentClassName="w-full"
      >
        <div className="reviewScopeHeader border-b border-[--base-border] px-4 py-[--spacing-10] sm:px-[--spacing-17]">
          <div className="headerContent">
            <div className="title">
              <h1>Review the Project Scope and Milestones</h1>
            </div>
            {active && (
              <div className="scopeActions">
                <div
                  className={
                    'decisionTabContainer ' +
                    (decision === 'reject'
                      ? 'border-[--state-danger-active]'
                      : decision === 'accept'
                        ? 'border-[--state-brand-active]'
                        : 'border-[--state-brand-active]')
                  }
                >
                  {(['accept', 'reject'] as const).map((value) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={decision === value}
                      disabled={locked}
                      onClick={() =>
                        setDraft({
                          ...draft,
                          decision: decision === value ? null : value,
                          reviewKey,
                          proposalRevision: proposal.revision,
                        })
                      }
                      className={
                        'decisionTab ' +
                        (decision === value
                          ? value === 'accept'
                            ? 'accepted'
                            : 'rejected'
                          : 'unselected')
                      }
                    >
                      <i
                        aria-hidden="true"
                        className={
                          value === 'accept' ? 'ri-check-line' : 'ri-close-line'
                        }
                      />
                      {value === 'accept' ? 'Accept' : 'Reject'}
                    </button>
                  ))}
                </div>
                <Button
                  disabled={
                    locked ||
                    !decision ||
                    (!delivery && !execution) ||
                    presentation.isPending ||
                    presentation.isError
                  }
                  onClick={() => void submit()}
                >
                  Submit Response
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="pageContent scopeReviewPageContent space-y-6 px-4 py-8 sm:px-[--spacing-17]">
          <p className="text-left text-sm text-[--text-dark-secondary]">
            {active
              ? delivery
                ? 'Accepting the delivered plan does not move funds. Execution approval is a separate next step.'
                : 'The planning delivery is accepted. Accepting now locks ' +
                  cost +
                  ' ' +
                  currency.symbol +
                  ' for execution.'
              : proposal.status === 'Draft'
                ? 'Changes have been requested. The coordinator can revise and resubmit the scope.'
                : 'This scope is ' + proposal.status + '.'}
          </p>
          {(notice || command.status) && (
            <p role="status" className="text-left">
              {notice || command.status}
            </p>
          )}
          {command.error && (
            <p role="alert" className="text-left">
              {command.error}
            </p>
          )}
          {writer.error && (
            <p role="alert" className="text-left">
              {writer.error}
            </p>
          )}
          {writer.pending && (
            <div className="text-left">
              <p>
                Your business decision will not be repeated. Save the pending
                comment separately.
              </p>
              <Button disabled={writer.busy} onClick={() => void writer.send()}>
                Retry response
              </Button>
            </div>
          )}
          {(writer.pending || writer.blocked) && (
            <div className="space-y-3 text-left">
              <p className="text-sm text-[--text-dark-secondary]">
                Discarding clears only this local response. Check the history
                first: a response with a lost reply may already be saved.
                Confirmed project decisions remain unchanged.
              </p>
              <Button
                variant="outline"
                disabled={writer.busy}
                onClick={writer.discard}
              >
                Discard local response
              </Button>
            </div>
          )}
          {command.pending && (
            <Button
              disabled={command.busy || command.blocked}
              onClick={() => void command.run(null).then(finish)}
            >
              Check pending response
            </Button>
          )}
          {execution && decision === 'accept' && (
            <div className="flex flex-wrap gap-3 text-left text-sm">
              <p>
                {balance.isPending
                  ? 'Loading balance…'
                  : balance.isError
                    ? 'Could not load balance.'
                    : 'Available balance: ' +
                      available +
                      ' ' +
                      currency.symbol +
                      ' · Required: ' +
                      cost +
                      ' ' +
                      currency.symbol}
              </p>
              <Link
                className="text-primary underline"
                to={'/payments/' + project.projectId + '/fund'}
              >
                View balance and funding
              </Link>
              {balance.isError && (
                <Button
                  variant="secondary"
                  onClick={() => void balance.refetch()}
                >
                  Retry balance
                </Button>
              )}
            </div>
          )}
          <div className="reviewScopeContent text-left">
            <div className="infoCol min-w-0">
              {presentation.isError && (
                <div role="alert">
                  <p>{presentation.error.message}</p>
                  <Button onClick={() => void presentation.refetch()}>
                    Retry milestone details
                  </Button>
                </div>
              )}
              <ProposalMilestones
                delivery={proposal.delivery}
                definition={definition}
                presentation={presentation.data}
                milestones={proposal.milestones}
              />
            </div>
            <div className="reviewCol min-w-0 space-y-6">
              <div
                className={
                  'flex flex-col gap-[--spacing-8] rounded-[--radi-5] border bg-[--base-surface-2] p-[--spacing-10] ' +
                  (decision === 'accept'
                    ? 'border-[--state-brand-active]'
                    : decision === 'reject'
                      ? 'border-[--state-danger-active]'
                      : 'border-[--state-brand-active]')
                }
              >
                {participants.isError && (
                  <div role="alert">
                    <p>{participants.error.message}</p>
                    <Button onClick={() => void participants.refetch()}>
                      Retry participants
                    </Button>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <Avatar
                    size="lg"
                    alt={name}
                    src={
                      coordinator?.workerImage && coordinator.profiles
                        ? profileImageUrl(
                            coordinator.profiles.principalId,
                            'worker'
                          )
                        : undefined
                    }
                  />
                  <div>
                    <p className="text-base font-semibold">{name}'s Proposal</p>
                    <p className="text-xs text-[--text-dark-tertiary]">
                      Your Project Leader
                    </p>
                  </div>
                </div>
                <p className="whitespace-pre-wrap text-base text-[--text-dark-secondary]">
                  {lastMessage || 'No proposal comment provided.'}
                </p>
                <div className="flex flex-col gap-3 border-t border-[--base-border] pt-3">
                  {brief.data?.brief.indicativeBudget && (
                    <div className="flex justify-between gap-4 text-sm">
                      <span>Your indicative budget (USD)</span>
                      <span>
                        {
                          (
                            {
                              Below10000: 'Below $10,000',
                              From10000To50000: '$10,000–$50,000',
                              From50000To100000: '$50,000–$100,000',
                              Above100000: 'Above $100,000',
                            } as const
                          )[brief.data.brief.indicativeBudget.range]
                        }
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between gap-4 text-sm">
                    <span>Project actual cost</span>
                    <strong>{cost} {currency.symbol}</strong>
                  </div>
                  <div className="flex justify-between gap-4 text-sm">
                    <span>Delivery Date</span>
                    <strong>
                      {deliveryPreferenceLabel(
                        proposal.delivery ?? brief.data?.brief.delivery
                      )}
                    </strong>
                  </div>
                </div>
              </div>
              {active && (
                <div className="space-y-3 rounded-[--radi-5] border border-[--base-border] bg-[--base-surface-2] p-[--spacing-10]">
                  <label htmlFor="client-comment" className="font-semibold">
                    Add Comment{' '}
                    <span className="text-xs font-normal text-[--text-dark-tertiary]">
                      (
                      {decision === 'reject'
                        ? 'required for changes'
                        : 'optional'}
                      )
                    </span>
                  </label>
                  <textarea
                    id="client-comment"
                    rows={4}
                    disabled={locked}
                    value={draft.message}
                    onChange={(event) =>
                      setDraft({ ...draft, message: event.target.value })
                    }
                    placeholder="Add a comment"
                    className="w-full resize-y rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-1] p-3 text-sm"
                  />
                </div>
              )}
              <div className="space-y-4 rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-2] p-[--spacing-10]">
                <div className="flex gap-3">
                  <div
                    aria-hidden="true"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[--colors-emerald-200] text-[--colors-alpha-dark-900]"
                  >
                    <i className="ri-question-line text-xl" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold">
                      Not sure if to accept or reject?
                    </h2>
                    <p className="text-xs text-[--text-dark-tertiary]">
                      Contact the Project Leader to further investigate.
                    </p>
                  </div>
                </div>
                {coordinator?.contactEmail && (
                  <div className="flex items-center justify-between gap-3 rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-1] px-4 py-3 text-sm">
                    <span className="break-all">
                      {coordinator.contactEmail}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="fit"
                      aria-label={
                        emailCopied ? 'Email copied' : 'Copy coordinator email'
                      }
                      onClick={() =>
                        void navigator.clipboard
                          .writeText(coordinator.contactEmail!)
                          .then(() => setEmailCopied(true))
                          .catch(() =>
                            setNotice('Could not copy the email address.')
                          )
                      }
                    >
                      <i
                        aria-hidden="true"
                        className={
                          emailCopied ? 'ri-check-line' : 'ri-file-copy-line'
                        }
                      />
                    </Button>
                    {emailCopied && (
                      <span role="status" className="sr-only">
                        Email copied
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <ProposalReviewHistory
            projectId={project.projectId}
            proposal={proposal}
            allowReply={false}
          />
          <Link
            className="block text-left text-primary underline"
            to={'/projects/' + project.projectId}
          >
            Back to Project
          </Link>
        </div>
      </MainScreen>
    </>
  );
}
